import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, CreditCard, ShieldAlert, Smartphone,
  TrendingUp, RefreshCw, Search, Calendar
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const USERS_PER_PAGE = 15;

const PLATFORM_COLORS = { ios: '#3b82f6', android: '#10b981', web: '#f59e0b' };

const VaultUserAnalytics = ({ fetchWithAuth }) => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [overview, setOverview] = useState(null);

  // Table state
  const [userList, setUserList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');

  const searchTimeout = useRef(null);

  const fetchOverview = useCallback(async (d) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/admin/vault-users/overview?days=${d}`);
      if (res.ok) setOverview(await res.json());
    } catch (e) { console.error('vault-users overview:', e); }
  }, [fetchWithAuth]);

  const doFetchList = useCallback(async (p, s, ut, pf, sf) => {
    setListLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: USERS_PER_PAGE });
      if (s) params.set('search', s);
      if (ut !== 'all') params.set('user_type', ut);
      if (pf !== 'all') params.set('platform', pf);
      if (sf !== 'all') params.set('subscription_status', sf);
      const res = await fetchWithAuth(`${BACKEND_URL}/api/admin/vault-users/list?${params}`);
      if (res.ok) {
        const data = await res.json();
        setUserList(data.users || []);
        setTotalCount(data.total || 0);
      }
    } catch (e) { console.error('vault-users list:', e); }
    finally { setListLoading(false); }
  }, [fetchWithAuth]);

  // Overview reloads when days changes; also resets page to 1
  useEffect(() => {
    let mounted = true;
    setPage(1);
    setLoading(true);
    fetchOverview(days).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [days]); // eslint-disable-line

  // List reloads when page or any filter changes (search is debounced separately)
  useEffect(() => {
    doFetchList(page, search, userTypeFilter, platformFilter, subscriptionFilter);
  }, [page, userTypeFilter, platformFilter, subscriptionFilter]); // eslint-disable-line

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      fetchOverview(days),
      doFetchList(page, search, userTypeFilter, platformFilter, subscriptionFilter),
    ]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    setPage(1);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      doFetchList(1, val, userTypeFilter, platformFilter, subscriptionFilter);
    }, 400);
  };

  // KPI computations
  const activePremium = (overview?.subscriptions?.active || 0) + (overview?.subscriptions?.trial || 0);
  const pendingCompliance = overview?.document_status?.pending_review || 0;
  const mobileShare = overview?.total_users
    ? Math.round(((overview.platforms?.ios || 0) + (overview.platforms?.android || 0)) / overview.total_users * 100)
    : 0;

  const platformChartData = overview ? [
    { platform: 'iOS',     users: overview.platforms?.ios     || 0, fill: PLATFORM_COLORS.ios },
    { platform: 'Android', users: overview.platforms?.android || 0, fill: PLATFORM_COLORS.android },
    { platform: 'Web',     users: overview.platforms?.web     || 0, fill: PLATFORM_COLORS.web },
  ] : [];

  const totalPages = Math.ceil(totalCount / USERS_PER_PAGE);

  const tooltipStyle = {
    backgroundColor: 'var(--background)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    fontSize: '12px',
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading Vault user analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Vault User Analytics</h2>
          <p className="text-muted-foreground mt-2">
            App signups, user type distribution, device usage, and subscription statuses
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(days)} onValueChange={v => setDays(parseInt(v, 10))}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading || listLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${(loading || listLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Registered Users"
          value={overview?.total_users ?? '—'}
          icon={Users}
          color="blue"
          subtitle="All time"
        />
        <StatCard
          title="Active Premium Subscriptions"
          value={activePremium}
          icon={CreditCard}
          color="green"
          subtitle="Active + Trial"
        />
        <StatCard
          title="Compliance Pending Approval"
          value={pendingCompliance}
          icon={ShieldAlert}
          color="orange"
          subtitle="Documents under review"
        />
        <StatCard
          title="Mobile App Share"
          value={`${mobileShare}%`}
          icon={Smartphone}
          color="purple"
          subtitle="iOS + Android users"
          isText
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Registration Growth Trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Registration Growth — Last {days} Days
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!overview?.registration_trends?.length ? (
              <EmptyState message="No registration trend data available." />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={overview.registration_trends}>
                  <defs>
                    <linearGradient id="gradCarriers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradOwnerOps" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradDrivers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                  <Area type="monotone" dataKey="carriers"        stroke="#3b82f6" fill="url(#gradCarriers)" strokeWidth={2} name="Carriers" />
                  <Area type="monotone" dataKey="owner_operators" stroke="#10b981" fill="url(#gradOwnerOps)" strokeWidth={2} name="Owner-Operators" />
                  <Area type="monotone" dataKey="drivers"         stroke="#f59e0b" fill="url(#gradDrivers)"  strokeWidth={2} name="Drivers" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Platform Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-purple-500" />
              Platform Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!overview?.platforms ? (
              <EmptyState message="No platform data available." />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={platformChartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="platform" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="users" name="Users" radius={[4, 4, 0, 0]}>
                      {platformChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-2">
                  {platformChartData.map(({ platform, users, fill }) => (
                    <div key={platform} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: fill }} />
                        <span className="text-muted-foreground">{platform}</span>
                      </div>
                      <span className="font-medium">{users.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

      </div>

      {/* User Directory Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-500" />
              User Directory
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              {totalCount.toLocaleString()} user{totalCount !== 1 ? 's' : ''}
            </span>
          </div>
        </CardHeader>
        <CardContent>

          {/* Search + Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={userTypeFilter} onValueChange={v => { setUserTypeFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[155px]">
                <SelectValue placeholder="User Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="carrier">Carrier</SelectItem>
                <SelectItem value="owner_operator">Owner-Operator</SelectItem>
                <SelectItem value="driver">Driver</SelectItem>
              </SelectContent>
            </Select>
            <Select value={platformFilter} onValueChange={v => { setPlatformFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[135px]">
                <SelectValue placeholder="Platform" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                <SelectItem value="ios">iOS</SelectItem>
                <SelectItem value="android">Android</SelectItem>
                <SelectItem value="web">Web</SelectItem>
              </SelectContent>
            </Select>
            <Select value={subscriptionFilter} onValueChange={v => { setSubscriptionFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[155px]">
                <SelectValue placeholder="Subscription" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {listLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : !userList.length ? (
            <EmptyState message="No users match your search." />
          ) : (
            <>
              {/* Table header */}
              <div className="grid grid-cols-12 text-xs font-medium text-muted-foreground px-3 pb-2 border-b border-border">
                <span className="col-span-2">Name</span>
                <span className="col-span-2">Email</span>
                <span className="col-span-2">Phone</span>
                <span className="col-span-1">Role Type</span>
                <span className="col-span-1 text-center">Platform</span>
                <span className="col-span-1 text-center">Subscription</span>
                <span className="col-span-1">Registered</span>
                <span className="col-span-2 text-center">Verification</span>
              </div>

              {userList.map((user, idx) => {
                const fullName = user.name
                  || `${user.first_name || ''} ${user.last_name || ''}`.trim()
                  || '—';
                return (
                  <div
                    key={user.id || idx}
                    className="grid grid-cols-12 items-center px-3 py-3 border-b border-border/50 hover:bg-muted/40 transition-colors"
                  >
                    <div className="col-span-2">
                      <p className="text-sm font-medium truncate">{fullName}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground truncate">{user.email || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground truncate">{user.phone || '—'}</p>
                    </div>
                    <div className="col-span-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 capitalize whitespace-nowrap">
                        {formatUserType(user.user_type || user.role)}
                      </span>
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <PlatformBadge platform={user.platform} />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <SubscriptionBadge status={user.subscription_status || user.subscription_tier} />
                    </div>
                    <div className="col-span-1">
                      <p className="text-xs text-muted-foreground whitespace-nowrap">
                        {user.created_at ? formatDate(user.created_at) : '—'}
                      </p>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <VerificationBadge status={user.verification_status} />
                    </div>
                  </div>
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} · {totalCount.toLocaleString()} total
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

        </CardContent>
      </Card>

    </div>
  );
};

// --- Sub-components ---

const StatCard = ({ title, value, icon: Icon, color, subtitle, isText = false }) => {
  const colorClasses = {
    blue:   'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    green:  'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  };
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1 flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className={`font-bold text-foreground ${isText ? 'text-2xl' : 'text-3xl'}`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ml-3 ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PlatformBadge = ({ platform }) => {
  const map = {
    ios:     { label: 'iOS',     cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
    android: { label: 'Android', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    web:     { label: 'Web',     cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  };
  const entry = map[platform?.toLowerCase()] || { label: platform || '—', cls: 'bg-muted text-muted-foreground' };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${entry.cls}`}>
      {entry.label}
    </span>
  );
};

const SubscriptionBadge = ({ status }) => {
  const map = {
    active:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    trial:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    free:    'bg-muted text-muted-foreground',
    expired: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  const cls = map[status?.toLowerCase()] || 'bg-muted text-muted-foreground';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize whitespace-nowrap ${cls}`}>
      {status || '—'}
    </span>
  );
};

const VerificationBadge = ({ status }) => {
  const map = {
    verified:       { label: 'Verified',   cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    pending:        { label: 'Pending',    cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-500' },
    pending_review: { label: 'In Review',  cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    rejected:       { label: 'Rejected',   cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    unverified:     { label: 'Unverified', cls: 'bg-muted text-muted-foreground' },
  };
  const entry = map[status?.toLowerCase()] || { label: status || '—', cls: 'bg-muted text-muted-foreground' };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${entry.cls}`}>
      {entry.label}
    </span>
  );
};

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
    <Users className="w-10 h-10 opacity-25" />
    <p className="text-sm">{message}</p>
  </div>
);

const formatUserType = (type) => {
  if (!type) return '—';
  return type.replace(/_/g, ' ');
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
};

export default VaultUserAnalytics;
